/**
 * Status is the only place the palette leaves its single brand accent, so every
 * semantic color in the app resolves through this one module. The classes map
 * onto the `--status-*` tokens in `index.css`; `src/theme.test.ts` holds each
 * text/background pair at WCAG AA contrast.
 */

export type StatusVariant =
  "queued" | "running" | "waiting" | "succeeded" | "failed" | "skipped" | "paused";

export const STATUS_VARIANTS: readonly StatusVariant[] = [
  "queued",
  "running",
  "waiting",
  "succeeded",
  "failed",
  "skipped",
  "paused",
];

/** Badge fill plus text color, for a labelled status pill. */
export const STATUS_BADGE: Record<StatusVariant, string> = {
  queued: "bg-status-queued-muted text-status-queued-foreground",
  running: "bg-status-running-muted text-status-running-foreground",
  waiting: "bg-status-waiting-muted text-status-waiting-foreground",
  succeeded: "bg-status-succeeded-muted text-status-succeeded-foreground",
  failed: "bg-status-failed-muted text-status-failed-foreground",
  skipped: "bg-status-skipped-muted text-status-skipped-foreground",
  paused: "bg-status-paused-muted text-status-paused-foreground",
};

/** Solid fill for the small dot that precedes a status label. */
export const STATUS_DOT: Record<StatusVariant, string> = {
  queued: "bg-status-queued",
  running: "bg-status-running",
  waiting: "bg-status-waiting",
  succeeded: "bg-status-succeeded",
  failed: "bg-status-failed",
  skipped: "bg-status-skipped",
  paused: "bg-status-paused",
};

/** Text-only color, for inline figures such as a failure count. */
export const STATUS_TEXT: Record<StatusVariant, string> = {
  queued: "text-status-queued-foreground",
  running: "text-status-running-foreground",
  waiting: "text-status-waiting-foreground",
  succeeded: "text-status-succeeded-foreground",
  failed: "text-status-failed-foreground",
  skipped: "text-status-skipped-foreground",
  paused: "text-status-paused-foreground",
};

/**
 * Surfaces that are *not* run status — transport mode, tool turns — must not
 * borrow the `--status-*` ramp, or a green "ws" badge reads as a succeeded run.
 * They resolve through the neutral and brand tokens instead, which keeps run
 * status the only semantically coloured surface in the product.
 */
export type SurfaceVariant = "brand" | "neutral";

export const SURFACE_BADGE: Record<SurfaceVariant, string> = {
  brand: "bg-brand/10 text-brand",
  neutral: "bg-muted text-muted-foreground",
};

/** Live streaming reads as the brand accent; the polling fallback reads neutral. */
export const TRANSPORT_BADGE: Readonly<Record<"ws" | "poll", SurfaceVariant>> = {
  ws: "brand",
  poll: "neutral",
};

/**
 * Run, session, sandbox-build, agent and integration statuses all narrow to the
 * same seven variants. Unknown values fall back to `skipped`, the neutral grey,
 * so a status the backend adds later renders as a plain pill rather than
 * borrowing a color that means something else.
 */
const BY_STATUS = new Map<string, StatusVariant>([
  ["queued", "queued"],
  ["pending", "queued"],
  ["starting", "running"],
  ["running", "running"],
  ["building", "waiting"],
  ["waiting", "waiting"],
  ["needs_reauth", "waiting"],
  ["succeeded", "succeeded"],
  ["completed", "succeeded"],
  ["applied", "succeeded"],
  ["ready", "succeeded"],
  ["active", "succeeded"],
  ["enabled", "succeeded"],
  ["connected", "succeeded"],
  ["failed", "failed"],
  ["timed_out", "failed"],
  ["revoked", "failed"],
  ["paused", "paused"],
  ["dry_run", "running"],
]);

export function statusVariant(status: string): StatusVariant {
  return BY_STATUS.get(status.toLowerCase()) ?? "skipped";
}
